<template>
  <div>
    <div
      class="w-full mb-3 flex items-center"
      :class="[
        {
          'opacity-100': eligible && !isLoadingAddress,
          'cursor-not-allowed opacity-50': !eligible
        },
        eligible && isLoadingAddress ? 'cursor-wait' : eligible ? 'cursor-pointer' : ''
      ]"
    >
      <input
        :id="'deliveryAddress' + deliveryAddress.id"
        :value="deliveryAddress.id"
        :name="`delivery-address${bottle.id}`"
        type="radio"
        class="flex-shrink-0 form-radio h-4 w-4 text-brand-600 transition duration-150 ease-in-out"
        :class="{
          'cursor-pointer': eligible && !isLoadingAddress,
          'cursor-not-allowed': !eligible,
          'cursor-wait': isLoadingAddress
        }"
        :checked="selected"
        :disabled="isLoadingAddress || locked"
        @click="selectDeliveryAddress"
      />
      <label
        :for="'deliveryAddress' + deliveryAddress.id"
        class="ml-3 w-full min-h-[2.5rem] flex items-center"
        :class="{
          'cursor-pointer': eligible && !isLoadingAddress,
          'cursor-not-allowed': !eligible,
          'cursor-wait': isLoadingAddress
        }"
        @click="selectDeliveryAddress"
      >
        <template v-if="isLoadingAddress">
          <svg
            class="animate-spin h-5 w-5 text-brand-500 flex-shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </template>
        <span v-else>
          <span class="text-md font-semibold text-brand-gray-800 tracking-tight mr-2">{{ topAddressLine }}</span>
          <span
            v-if="isDefault"
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2"
          >
            Default
          </span>
          <span
            v-if="!eligible"
            class="text-xxs text-gray-brand-900 font-medium font-manrope uppercase p-1 px-1 bg-gray-brand-300 rounded-full tracking-widest no-wrap truncate"
          >
            {{ closedWithFutureSlots ? 'Closed — reopens soon' : 'Not available' }}
          </span>
          <br />
          <span class="text-sm text-brand-gray-600 tracking-wide">{{ bottomAddressLine }}</span>
          <br v-if="notes" />
          <span class="text-sm text-brand-gray-600 tracking-wide">{{ notes }}</span>
        </span>
      </label>

      <button
        v-if="!isLoadingAddress && !locked"
        class="ml-2 inline-flex items-center shadow-sm p-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-full bg-white hover:border-blue-600 transition-all duration-150 group-hover:opacity-100 text-gray-500 hover:text-blue-600"
        @click="editDeliveryAddress()"
      >
        <PencilIcon class="h-4 w-4" aria-hidden="true" />
      </button>
      <button
        v-if="!isLoadingAddress && !locked"
        class="ml-2 inline-flex items-center shadow-sm p-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-full bg-white hover:border-blue-600 transition-all duration-150 group-hover:opacity-100 text-gray-500 hover:text-blue-600"
        @click="removeDeliveryAddress()"
      >
        <XIcon class="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
    <div v-if="closedWithFutureSlots" class="ml-7 mb-3">
      <div v-for="entry in futureSlotsByMethod" :key="entry.method.id" class="mt-2">
        <div class="text-sm font-semibold text-brand-gray-800 tracking-tight">
          {{ entry.method.attributes.name }}
        </div>
        <div
          v-if="!entry.method?.attributes?.hide_times"
          class="overflow-x-auto flex justify-start space-x-2 py-4 pl-2 -ml-2 pr-4"
        >
          <InfoRowSlotDay v-for="slot in entry.futureSlots" :key="slot.date" :option="slot" :selected="false" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PencilIcon, XIcon } from '@heroicons/vue/solid'
import mapRelationship from '@/utils/mapRelationship'
import InfoRowSlotDay from '@/components/Checkout/InfoRowSlotDay'
import { hasFutureSlots } from '@/utils/fulfillmentSlots'
export default {
  name: 'DeliveryAddressChoice',
  components: {
    PencilIcon,
    XIcon,
    InfoRowSlotDay
  },
  props: {
    deliveryAddress: { type: Object, required: true },
    bottle: { type: Object, required: true },
    loadingDeliveryAddressId: { type: [String, Number], default: null },
    showInlineDeliveryLoading: { type: Boolean, default: false },
    locked: { type: Boolean, default: false }
  },
  data: () => ({}),
  computed: {
    isLoadingAddress() {
      return (
        this.showInlineDeliveryLoading &&
        this.loadingDeliveryAddressId != null &&
        this.deliveryAddress?.id == this.loadingDeliveryAddressId
      )
    },
    topAddressLine() {
      const a = this.deliveryAddress.attributes
      return [a.address1, a.address2].filter(Boolean).join(', ')
    },
    bottomAddressLine() {
      const a = this.deliveryAddress.attributes
      return [a.city, a.state, a.zip].filter(Boolean).join(', ')
    },
    id() {
      this.deliveryAddress.id
    },
    notes() {
      return this.deliveryAddress.attributes.notes
    },
    eligible() {
      return this.deliveryAddress.attributes.eligible
    },
    // Resolves the fulfillment methods referenced by the address's eligible_fulfillment_methods relationship.
    eligibleFulfillmentMethodsForAddress() {
      const stubs = this.deliveryAddress?.relationships?.eligible_fulfillment_methods?.data || []
      return stubs.map((stub) => mapRelationship(this.$store.state, stub)).filter(Boolean)
    },
    // Per-method list of future slot entries (hasnt_started_yet: true).
    futureSlotsByMethod() {
      const resolver = (stub) => mapRelationship(this.$store.state, stub)
      return this.eligibleFulfillmentMethodsForAddress
        .map((method) => {
          const stub = method?.relationships?.fulfillment_slots_detail?.data
          if (!stub) return null
          const detail = resolver(stub)
          const slots = detail?.attributes?.calculated_fulfillment_slots || []
          if (!hasFutureSlots(slots)) return null
          const futureSlots = slots.filter((s) => s?.hasnt_started_yet === true)
          return { method, futureSlots }
        })
        .filter(Boolean)
    },
    // True when the address is !eligible but at least one in-zone method has future slots.
    // This is the "closed, reopens on X" state.
    closedWithFutureSlots() {
      return !this.eligible && this.futureSlotsByMethod.length > 0
    },
    selected() {
      return this.bottle?.relationships?.delivery_address?.data?.id == this.deliveryAddress?.id
    },
    isDefault() {
      return !!this.deliveryAddress.attributes.is_default
    }
  },
  methods: {
    removeDeliveryAddress() {
      this.$store
        .dispatch('removeDeliveryAddress', {
          deliveryAddressID: this.deliveryAddress.id,
          bottleId: this.bottle.id
        })
        .then(() => {
          this.$emit('deliveryAddressActionExecuted')
        })
    },
    editDeliveryAddress() {
      this.$emit('deliveryAddressChangeClicked', this.deliveryAddress)
    },
    selectDeliveryAddress() {
      if (this.locked) return
      if (this.eligible && !this.isLoadingAddress) {
        if (this.showInlineDeliveryLoading) {
          this.$emit('loadingDeliveryAddressId', this.deliveryAddress.id)
        }
        this.$emit('showLoading', true)
        this.$store
          .dispatch('setDeliveryAddressID', { deliveryAddressID: this.deliveryAddress.id, bottle: this.bottle })
          .then(
            () => {
              this.$emit('loadingDeliveryAddressId', null)
              this.$emit('showLoading', false)
              this.$emit('clickedNewMethod')
            },
            () => {
              this.$emit('loadingDeliveryAddressId', null)
              this.$emit('showLoading', false)
            }
          )
      }
    }
  }
}
</script>
